// ./src/components/generic/PolygonMapEditor/PolygonMapEditor.tsx
import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
} from "react";
import {
  Modal,
  Stack,
  Group,
  Text,
  Paper,
  ActionIcon,
  ScrollArea,
  Box,
  TextInput,
  Tooltip,
  Badge,
  Alert,
} from "@mantine/core";
import { AppButton, AppIcon } from "@/components/generic";
import { useGoogleMapsContext } from "@/contexts";

export interface PolygonPoint {
  lat: number;
  lng: number;
}

export interface PolygonWithTitle {
  title: string;
  points: PolygonPoint[];
}

export interface PolygonMapEditorProps {
  /** Whether the editor modal is open */
  opened: boolean;
  /** Callback when modal closes */
  onClose: () => void;
  /** Current polygons value */
  value?: PolygonWithTitle[];
  /** Callback when polygons change */
  onChange: (polygons: PolygonWithTitle[] | undefined) => void;
  /** Custom title for the modal */
  title?: string;
  /** Maximum number of polygons allowed (null = unlimited) */
  maxPolygons?: number | null;
  /** Maximum number of points per polygon (null = unlimited) */
  maxPointsPerPolygon?: number | null;
  /** Maximum total number of points across all polygons (null = unlimited) */
  maxPoints?: number | null;
}

interface PolygonData {
  id: string;
  name: string;
  points: PolygonPoint[];
  color: string;
}

// Predefined colors for polygons
const POLYGON_COLORS = [
  "#4CAF50", // Green
  "#2196F3", // Blue
  "#FF9800", // Orange
  "#9C27B0", // Purple
  "#F44336", // Red
  "#00BCD4", // Cyan
  "#FFEB3B", // Yellow
  "#795548", // Brown
  "#607D8B", // Blue Grey
  "#E91E63", // Pink
];

const generateId = () =>
  `polygon-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

const getPolygonColor = (index: number) =>
  POLYGON_COLORS[index % POLYGON_COLORS.length];

const formatCoordinate = (value: number, decimals: number = 6) => {
  return value.toFixed(decimals);
};

// Default center (USA) if no polygons and no user location
const DEFAULT_CENTER = { lat: 40, lng: -100 };
const DEFAULT_ZOOM = 4;

export const PolygonMapEditor: React.FC<PolygonMapEditorProps> = ({
  opened,
  onClose,
  value,
  onChange,
  title = "Edit Polygons",
  maxPolygons = null,
  maxPointsPerPolygon = null,
  maxPoints = null,
}) => {
  const { loadLibraries } = useGoogleMapsContext();

  // State
  const [polygons, setPolygons] = useState<PolygonData[]>([]);
  const [selectedPolygonId, setSelectedPolygonId] = useState<string | null>(
    null
  );
  const [activePointIndex, setActivePointIndex] = useState<number | null>(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [drawingPoints, setDrawingPoints] = useState<PolygonPoint[]>([]);
  const [mapLoaded, setMapLoaded] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isInitializing, setIsInitializing] = useState(false);

  // Refs
  const mapContainerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<google.maps.Map | null>(null);
  const polygonObjectsRef = useRef<Map<string, google.maps.Polygon>>(new Map());
  const markersRef = useRef<
    Map<string, google.maps.marker.AdvancedMarkerElement[]>
  >(new Map());
  const edgePolylinesRef = useRef<google.maps.Polyline[]>([]);
  const drawingMarkersRef = useRef<google.maps.marker.AdvancedMarkerElement[]>(
    []
  );
  const drawingPolylineRef = useRef<google.maps.Polyline | null>(null);
  const mapsLibRef = useRef<typeof google.maps | null>(null);
  const markerLibRef = useRef<google.maps.MarkerLibrary | null>(null);
  const mapClickListenerRef = useRef<google.maps.MapsEventListener | null>(
    null
  );
  const initializedRef = useRef(false);

  // Computed limits
  const totalPointsCount = useMemo(() => {
    return (
      polygons.reduce((sum, p) => sum + p.points.length, 0) +
      drawingPoints.length
    );
  }, [polygons, drawingPoints]);

  const selectedPolygon = useMemo(() => {
    return polygons.find((p) => p.id === selectedPolygonId);
  }, [polygons, selectedPolygonId]);

  const canAddPolygon = useMemo(() => {
    if (maxPolygons !== null && polygons.length >= maxPolygons) return false;
    return true;
  }, [polygons.length, maxPolygons]);

  const canAddPointToDrawing = useMemo(() => {
    if (
      maxPointsPerPolygon !== null &&
      drawingPoints.length >= maxPointsPerPolygon
    )
      return false;
    if (maxPoints !== null && totalPointsCount >= maxPoints) return false;
    return true;
  }, [drawingPoints.length, totalPointsCount, maxPointsPerPolygon, maxPoints]);

  const canAddPointToSelectedPolygon = useMemo(() => {
    if (!selectedPolygon) return false;
    if (
      maxPointsPerPolygon !== null &&
      selectedPolygon.points.length >= maxPointsPerPolygon
    )
      return false;
    if (maxPoints !== null && totalPointsCount >= maxPoints) return false;
    return true;
  }, [selectedPolygon, totalPointsCount, maxPointsPerPolygon, maxPoints]);

  // Keep a ref for isDrawing to use in map click listener
  const isDrawingRef = useRef(isDrawing);
  useEffect(() => {
    isDrawingRef.current = isDrawing;
  }, [isDrawing]);

  // Calculate bounds for all polygons
  const calculateAllPolygonsBounds = useCallback(
    (polygonList: PolygonData[]) => {
      if (!polygonList.length) return null;

      const bounds = new google.maps.LatLngBounds();
      let hasPoints = false;

      polygonList.forEach((polygon) => {
        polygon.points.forEach((point) => {
          bounds.extend(point);
          hasPoints = true;
        });
      });

      return hasPoints ? bounds : null;
    },
    []
  );

  // Get user's current location
  const getUserLocation = useCallback((): Promise<PolygonPoint | null> => {
    return new Promise((resolve) => {
      if (!navigator.geolocation) {
        resolve(null);
        return;
      }

      navigator.geolocation.getCurrentPosition(
        (position) => {
          resolve({
            lat: position.coords.latitude,
            lng: position.coords.longitude,
          });
        },
        () => {
          resolve(null);
        },
        { timeout: 5000, enableHighAccuracy: false }
      );
    });
  }, []);

  // Fit map to show all polygons or user location
  const fitMapToContent = useCallback(
    async (polygonList: PolygonData[]) => {
      if (!mapRef.current) return;

      const bounds = calculateAllPolygonsBounds(polygonList);

      if (bounds) {
        // Fit to all polygons with padding
        mapRef.current.fitBounds(bounds, 50);
      } else {
        // No polygons - try to get user location
        const userLocation = await getUserLocation();

        if (userLocation) {
          mapRef.current.setCenter(userLocation);
          mapRef.current.setZoom(10);
        } else {
          // Fall back to default
          mapRef.current.setCenter(DEFAULT_CENTER);
          mapRef.current.setZoom(DEFAULT_ZOOM);
        }
      }
    },
    [calculateAllPolygonsBounds, getUserLocation]
  );

  // Initialize polygons from value prop
  useEffect(() => {
    if (opened && value && value.length > 0) {
      const initialPolygons: PolygonData[] = value.map((polygon, index) => ({
        id: generateId(),
        name: polygon.title || `Region ${index + 1}`,
        points: Array.isArray(polygon.points) ? [...polygon.points] : [],
        color: getPolygonColor(index),
      }));
      setPolygons(initialPolygons);
    } else if (opened) {
      setPolygons([]);
    }
  }, [opened, value]);

  // Cleanup function
  const cleanupMap = useCallback(() => {
    // Remove click listener
    if (mapClickListenerRef.current) {
      google.maps.event.removeListener(mapClickListenerRef.current);
      mapClickListenerRef.current = null;
    }

    // Clear all polygon objects
    polygonObjectsRef.current.forEach((poly) => poly.setMap(null));
    polygonObjectsRef.current.clear();

    // Clear all markers
    markersRef.current.forEach((markers) => {
      markers.forEach((marker) => (marker.map = null));
    });
    markersRef.current.clear();

    // Clear edge polylines
    edgePolylinesRef.current.forEach((polyline) => polyline.setMap(null));
    edgePolylinesRef.current = [];

    // Clear drawing elements
    drawingMarkersRef.current.forEach((marker) => (marker.map = null));
    drawingMarkersRef.current = [];

    if (drawingPolylineRef.current) {
      drawingPolylineRef.current.setMap(null);
      drawingPolylineRef.current = null;
    }

    // Clear map reference
    mapRef.current = null;
    setMapLoaded(false);
    initializedRef.current = false;
  }, []);

  // Initialize map when modal opens
  useEffect(() => {
    if (!opened) {
      return;
    }

    // Prevent double initialization
    if (isInitializing || initializedRef.current) {
      return;
    }

    const initMap = async () => {
      // Wait for container to be available
      if (!mapContainerRef.current) {
        return;
      }

      setIsInitializing(true);
      setError(null);

      try {
        const libs = await loadLibraries(["maps", "marker"]);
        mapsLibRef.current = libs.maps;
        markerLibRef.current = libs.marker;

        // Double-check container is still available after async load
        if (!mapContainerRef.current || !opened) {
          setIsInitializing(false);
          return;
        }

        // Create map
        const map = new libs.maps.Map(mapContainerRef.current, {
          center: DEFAULT_CENTER,
          zoom: DEFAULT_ZOOM,
          mapId: "polygon-editor-map",
          disableDefaultUI: false,
          zoomControl: true,
          mapTypeControl: true,
          streetViewControl: false,
          fullscreenControl: false,
        });

        mapRef.current = map;

        // Add click listener for drawing mode
        mapClickListenerRef.current = map.addListener(
          "click",
          (e: google.maps.MapMouseEvent) => {
            if (isDrawingRef.current && e.latLng) {
              addDrawingPoint({ lat: e.latLng.lat(), lng: e.latLng.lng() });
            }
          }
        );

        initializedRef.current = true;
        setMapLoaded(true);
        setIsInitializing(false);

        // Fit map to content after a short delay to ensure map is ready
        setTimeout(() => {
          if (value && value.length > 0) {
            const initialPolygons: PolygonData[] = value.map(
              (polygon, index) => ({
                id: generateId(),
                name: polygon.title || `Region ${index + 1}`,
                points: [...polygon.points],
                color: getPolygonColor(index),
              })
            );
            fitMapToContent(initialPolygons);
          } else {
            fitMapToContent([]);
          }
        }, 100);
      } catch (err) {
        console.error("Failed to load Google Maps:", err);
        setError("Failed to load Google Maps. Please check your API key.");
        setIsInitializing(false);
      }
    };

    // Small delay to ensure modal content is rendered
    const timeoutId = setTimeout(initMap, 50);

    return () => {
      clearTimeout(timeoutId);
    };
  }, [opened, loadLibraries, fitMapToContent, value]);

  // Cleanup on close
  useEffect(() => {
    if (!opened) {
      cleanupMap();
      setSelectedPolygonId(null);
      setActivePointIndex(null);
      setIsDrawing(false);
      setDrawingPoints([]);
      setError(null);
      setIsInitializing(false);
    }
  }, [opened, cleanupMap]);

  // Render polygons on map
  useEffect(() => {
    if (!mapLoaded || !mapRef.current || !mapsLibRef.current) return;

    // Clear existing polygon objects
    polygonObjectsRef.current.forEach((poly) => poly.setMap(null));
    polygonObjectsRef.current.clear();

    // Clear existing markers
    markersRef.current.forEach((markers) => {
      markers.forEach((marker) => (marker.map = null));
    });
    markersRef.current.clear();

    // Clear existing edge polylines
    edgePolylinesRef.current.forEach((polyline) => polyline.setMap(null));
    edgePolylinesRef.current = [];

    // Create new polygon objects
    polygons.forEach((polygonData) => {
      if (polygonData.points.length < 3) return;

      const isSelected = polygonData.id === selectedPolygonId;

      const polygon = new mapsLibRef.current!.Polygon({
        paths: polygonData.points,
        strokeColor: polygonData.color,
        strokeOpacity: isSelected ? 1 : 0.8,
        strokeWeight: isSelected ? 3 : 2,
        fillColor: polygonData.color,
        fillOpacity: isSelected ? 0.4 : 0.2,
        editable: false,
        draggable: false,
        map: mapRef.current,
      });

      // Click to select
      polygon.addListener("click", () => {
        if (!isDrawingRef.current) {
          setSelectedPolygonId(polygonData.id);
          setActivePointIndex(null);
        }
      });

      polygonObjectsRef.current.set(polygonData.id, polygon);

      // Add vertex markers and edge polylines for selected polygon
      if (isSelected && markerLibRef.current) {
        const markers: google.maps.marker.AdvancedMarkerElement[] = [];
        const points = polygonData.points;

        // Create invisible clickable edge polylines for adding points on double-click
        for (let i = 0; i < points.length; i++) {
          const startPoint = points[i];
          const endPoint = points[(i + 1) % points.length];
          const edgeIndex = i;

          const edgePolyline = new mapsLibRef.current!.Polyline({
            path: [startPoint, endPoint],
            strokeColor: "transparent",
            strokeOpacity: 0,
            strokeWeight: 20, // Wide clickable area
            map: mapRef.current,
            clickable: true,
          });

          // Double-click on edge to add a point
          edgePolyline.addListener(
            "dblclick",
            (e: google.maps.PolyMouseEvent) => {
              if (e.latLng) {
                const newPoint: PolygonPoint = {
                  lat: e.latLng.lat(),
                  lng: e.latLng.lng(),
                };
                // Insert point after the start point of this edge
                insertPointAfterIndex(polygonData.id, edgeIndex, newPoint);
              }
            }
          );

          edgePolylinesRef.current.push(edgePolyline);
        }

        // Create vertex markers
        points.forEach((point, index) => {
          const isActivePoint = activePointIndex === index;
          const dotSize = isActivePoint ? 18 : 14;

          // Simple dot element with marginBottom to center on anchor point
          const markerElement = document.createElement("div");
          markerElement.style.width = `${dotSize}px`;
          markerElement.style.height = `${dotSize}px`;
          markerElement.style.marginBottom = `-${dotSize / 2}px`;
          markerElement.style.backgroundColor = isActivePoint
            ? "#FFD700"
            : polygonData.color;
          markerElement.style.border = isActivePoint
            ? "3px solid #FFA500"
            : "2px solid white";
          markerElement.style.borderRadius = "50%";
          markerElement.style.cursor = "grab";
          markerElement.style.boxShadow = isActivePoint
            ? "0 0 8px rgba(255, 215, 0, 0.8), 0 2px 4px rgba(0,0,0,0.3)"
            : "0 2px 4px rgba(0,0,0,0.3)";
          markerElement.style.boxSizing = "border-box";

          if (isActivePoint) {
            markerElement.style.zIndex = "1000";
          }

          const marker = new markerLibRef.current!.AdvancedMarkerElement({
            position: point,
            map: mapRef.current,
            content: markerElement,
            gmpDraggable: true,
          });

          // Drag handling
          marker.addListener("dragstart", () => {
            setActivePointIndex(index);
          });

          marker.addListener("dragend", () => {
            const newPosition = marker.position;
            if (newPosition) {
              const lat =
                typeof newPosition.lat === "function"
                  ? newPosition.lat()
                  : newPosition.lat;
              const lng =
                typeof newPosition.lng === "function"
                  ? newPosition.lng()
                  : newPosition.lng;
              updatePolygonPoint(polygonData.id, index, { lat, lng });
            }
          });

          // Click to set as active point
          marker.addListener("click", () => {
            setActivePointIndex(index);
          });

          markers.push(marker);
        });

        markersRef.current.set(polygonData.id, markers);
      }
    });
  }, [polygons, selectedPolygonId, activePointIndex, mapLoaded]);

  // Render drawing preview
  useEffect(() => {
    if (!mapLoaded || !mapRef.current || !mapsLibRef.current) return;

    // Clear existing drawing elements
    drawingMarkersRef.current.forEach((marker) => (marker.map = null));
    drawingMarkersRef.current = [];
    if (drawingPolylineRef.current) {
      drawingPolylineRef.current.setMap(null);
      drawingPolylineRef.current = null;
    }

    if (!isDrawing || drawingPoints.length === 0) return;

    // Draw polyline preview
    const polyline = new mapsLibRef.current.Polyline({
      path: drawingPoints,
      strokeColor: "#4CAF50",
      strokeOpacity: 1,
      strokeWeight: 2,
      map: mapRef.current,
    });
    drawingPolylineRef.current = polyline;

    // Draw markers for each point
    if (markerLibRef.current) {
      drawingPoints.forEach((point, index) => {
        const dotSize = index === 0 ? 16 : 12;

        // Simple dot element with marginBottom to center on anchor point
        const markerElement = document.createElement("div");
        markerElement.style.width = `${dotSize}px`;
        markerElement.style.height = `${dotSize}px`;
        markerElement.style.marginBottom = `-${dotSize / 2}px`;
        markerElement.style.backgroundColor =
          index === 0 ? "#4CAF50" : "#81C784";
        markerElement.style.border = "2px solid white";
        markerElement.style.borderRadius = "50%";
        markerElement.style.boxShadow = "0 2px 4px rgba(0,0,0,0.3)";
        markerElement.style.boxSizing = "border-box";

        if (index === 0 && drawingPoints.length >= 3) {
          markerElement.style.cursor = "pointer";
          markerElement.title = "Click to close polygon";
        }

        const marker = new markerLibRef.current!.AdvancedMarkerElement({
          position: point,
          map: mapRef.current,
          content: markerElement,
        });

        // Click on first point to close polygon
        if (index === 0 && drawingPoints.length >= 3) {
          marker.addListener("click", () => {
            finishDrawing();
          });
        }

        drawingMarkersRef.current.push(marker);
      });
    }
  }, [isDrawing, drawingPoints, mapLoaded]);

  // Cleanup on close
  useEffect(() => {
    if (!opened) {
      // Clear all map objects
      polygonObjectsRef.current.forEach((poly) => poly.setMap(null));
      polygonObjectsRef.current.clear();
      markersRef.current.forEach((markers) => {
        markers.forEach((marker) => (marker.map = null));
      });
      markersRef.current.clear();
      drawingMarkersRef.current.forEach((marker) => (marker.map = null));
      drawingMarkersRef.current = [];
      if (drawingPolylineRef.current) {
        drawingPolylineRef.current.setMap(null);
        drawingPolylineRef.current = null;
      }

      // Reset state
      setSelectedPolygonId(null);
      setIsDrawing(false);
      setDrawingPoints([]);
      setMapLoaded(false);
      mapRef.current = null;
    }
  }, [opened]);

  // Actions
  const addDrawingPoint = useCallback(
    (point: PolygonPoint) => {
      // Check limits before adding
      if (!canAddPointToDrawing) return;
      setDrawingPoints((prev) => [...prev, point]);
    },
    [canAddPointToDrawing]
  );

  const startDrawing = useCallback(() => {
    if (!canAddPolygon) return;
    setIsDrawing(true);
    setDrawingPoints([]);
    setSelectedPolygonId(null);
    setActivePointIndex(null);
  }, [canAddPolygon]);

  const cancelDrawing = useCallback(() => {
    setIsDrawing(false);
    setDrawingPoints([]);
  }, []);

  const finishDrawing = useCallback(() => {
    if (drawingPoints.length >= 3) {
      const newPolygon: PolygonData = {
        id: generateId(),
        name: `Region ${polygons.length + 1}`,
        points: [...drawingPoints],
        color: getPolygonColor(polygons.length),
      };
      setPolygons((prev) => [...prev, newPolygon]);
      setSelectedPolygonId(newPolygon.id);
    }
    setIsDrawing(false);
    setDrawingPoints([]);
  }, [drawingPoints, polygons.length]);

  const updatePolygonPoint = useCallback(
    (polygonId: string, pointIndex: number, newPoint: PolygonPoint) => {
      setPolygons((prev) =>
        prev.map((p) =>
          p.id === polygonId
            ? {
                ...p,
                points: p.points.map((pt, i) =>
                  i === pointIndex ? newPoint : pt
                ),
              }
            : p
        )
      );
    },
    []
  );

  // Insert a point after a specific index (used for edge double-click)
  const insertPointAfterIndex = useCallback(
    (polygonId: string, afterIndex: number, newPoint: PolygonPoint) => {
      // Check limits
      const polygon = polygons.find((p) => p.id === polygonId);
      if (!polygon) return;
      if (
        maxPointsPerPolygon !== null &&
        polygon.points.length >= maxPointsPerPolygon
      )
        return;
      if (maxPoints !== null && totalPointsCount >= maxPoints) return;

      setPolygons((prev) =>
        prev.map((p) => {
          if (p.id !== polygonId) return p;

          const newPoints = [...p.points];
          // Insert after the specified index
          newPoints.splice(afterIndex + 1, 0, newPoint);
          return { ...p, points: newPoints };
        })
      );
      // Set the newly added point as active
      setActivePointIndex(afterIndex + 1);
    },
    [polygons, maxPointsPerPolygon, maxPoints, totalPointsCount]
  );

  const addPointToPolygon = useCallback(
    (polygonId: string) => {
      // Check limits
      if (!canAddPointToSelectedPolygon) return;

      const polygon = polygons.find((p) => p.id === polygonId);
      if (!polygon || polygon.points.length < 2) return;

      // Determine where to add the new point
      let insertAfterIndex: number;
      let point1: PolygonPoint;
      let point2: PolygonPoint;

      if (
        activePointIndex !== null &&
        activePointIndex < polygon.points.length
      ) {
        // Add after the active point
        insertAfterIndex = activePointIndex;
        point1 = polygon.points[activePointIndex];
        point2 = polygon.points[(activePointIndex + 1) % polygon.points.length];
      } else {
        // Default: add between last and first point
        insertAfterIndex = polygon.points.length - 1;
        point1 = polygon.points[polygon.points.length - 1];
        point2 = polygon.points[0];
      }

      const newPoint: PolygonPoint = {
        lat: (point1.lat + point2.lat) / 2,
        lng: (point1.lng + point2.lng) / 2,
      };

      setPolygons((prev) =>
        prev.map((p) => {
          if (p.id !== polygonId) return p;

          const newPoints = [...p.points];
          newPoints.splice(insertAfterIndex + 1, 0, newPoint);
          return { ...p, points: newPoints };
        })
      );

      // Set the newly added point as active
      setActivePointIndex(insertAfterIndex + 1);
    },
    [polygons, activePointIndex, canAddPointToSelectedPolygon]
  );

  const removePointFromPolygon = useCallback(
    (polygonId: string, pointIndex: number) => {
      setPolygons((prev) =>
        prev.map((p) =>
          p.id === polygonId
            ? { ...p, points: p.points.filter((_, i) => i !== pointIndex) }
            : p
        )
      );
    },
    []
  );

  const deletePolygon = useCallback((polygonId: string) => {
    setPolygons((prev) => prev.filter((p) => p.id !== polygonId));
    setSelectedPolygonId((prev) => (prev === polygonId ? null : prev));
  }, []);

  const renamePolygon = useCallback((polygonId: string, newName: string) => {
    setPolygons((prev) =>
      prev.map((p) => (p.id === polygonId ? { ...p, name: newName } : p))
    );
  }, []);

  const focusPolygon = useCallback(
    (polygonId: string) => {
      const polygon = polygons.find((p) => p.id === polygonId);
      if (!polygon || !mapRef.current || polygon.points.length === 0) return;

      setSelectedPolygonId(polygonId);
      setActivePointIndex(null);

      // Calculate bounds
      const bounds = new google.maps.LatLngBounds();
      polygon.points.forEach((point) => bounds.extend(point));
      mapRef.current.fitBounds(bounds, 50);
    },
    [polygons]
  );

  const handleSave = useCallback(() => {
    const result: PolygonWithTitle[] = polygons
      .filter((p) => p.points.length >= 3)
      .map((p) => ({
        title: p.name,
        points: p.points,
      }));

    onChange(result.length > 0 ? result : undefined);
    onClose();
  }, [polygons, onChange, onClose]);

  const handleCancel = useCallback(() => {
    onClose();
  }, [onClose]);

  return (
    <Modal
      opened={opened}
      onClose={handleCancel}
      fullScreen
      title={
        <Group gap="sm">
          <AppIcon iconName="MapPin" size={20} />
          <Text fw={600}>{title}</Text>
          {isDrawing && (
            <Badge color="green" variant="filled">
              Drawing Mode
            </Badge>
          )}
        </Group>
      }
      styles={{
        body: { padding: 0, height: "calc(100vh - 60px)" },
        header: { borderBottom: "1px solid var(--mantine-color-gray-3)" },
      }}
    >
      <Group align="stretch" h="100%" gap={0} wrap="nowrap">
        {/* Sidebar */}
        <Paper
          w={320}
          h="100%"
          style={{
            borderRight: "1px solid var(--mantine-color-gray-3)",
            flexShrink: 0,
          }}
        >
          <Stack h="100%" gap={0}>
            {/* Sidebar Header */}
            <Box
              p="md"
              style={{ borderBottom: "1px solid var(--mantine-color-gray-2)" }}
            >
              <Group justify="space-between" mb="sm">
                <Group gap="xs">
                  <Text size="sm" fw={600}>
                    Regions ({polygons.length}
                    {maxPolygons !== null ? `/${maxPolygons}` : ""})
                  </Text>
                  {maxPoints !== null && (
                    <Badge size="xs" variant="light" color="gray">
                      {totalPointsCount}/{maxPoints} pts
                    </Badge>
                  )}
                </Group>
                {!isDrawing ? (
                  <AppButton
                    size="xs"
                    variant="primary"
                    iconName="Plus"
                    onClick={startDrawing}
                    disabled={!canAddPolygon}
                  >
                    Draw New
                  </AppButton>
                ) : (
                  <Group gap="xs">
                    <AppButton
                      size="xs"
                      variant="secondary"
                      onClick={cancelDrawing}
                    >
                      Cancel
                    </AppButton>
                    <AppButton
                      size="xs"
                      variant="primary"
                      onClick={finishDrawing}
                      disabled={drawingPoints.length < 3}
                    >
                      Finish
                    </AppButton>
                  </Group>
                )}
              </Group>

              {isDrawing && (
                <Alert color="blue" variant="light" p="xs">
                  <Text size="xs">
                    Click on the map to add points. Click the first point or
                    press "Finish" to complete the polygon (
                    {drawingPoints.length}
                    {maxPointsPerPolygon !== null
                      ? `/${maxPointsPerPolygon}`
                      : ""}{" "}
                    points).
                    {!canAddPointToDrawing && (
                      <Text component="span" c="orange" fw={600}>
                        {" "}
                        Point limit reached.
                      </Text>
                    )}
                  </Text>
                </Alert>
              )}
            </Box>

            {/* Polygon List */}
            <ScrollArea flex={1} p="sm">
              {polygons.length === 0 && !isDrawing ? (
                <Text size="sm" c="dimmed" ta="center" py="xl">
                  No regions defined yet.
                  <br />
                  Click "Draw New" to create one.
                </Text>
              ) : (
                <Stack gap="xs">
                  {polygons.map((polygon) => (
                    <Paper
                      key={polygon.id}
                      p="sm"
                      withBorder
                      style={{
                        borderColor:
                          selectedPolygonId === polygon.id
                            ? polygon.color
                            : undefined,
                        borderWidth: selectedPolygonId === polygon.id ? 2 : 1,
                        cursor: "pointer",
                      }}
                      onClick={() => focusPolygon(polygon.id)}
                    >
                      <Group justify="space-between" mb="xs">
                        <Group gap="xs">
                          <Box
                            w={12}
                            h={12}
                            style={{
                              backgroundColor: polygon.color,
                              borderRadius: 2,
                            }}
                          />
                          <TextInput
                            value={polygon.name}
                            onChange={(e) =>
                              renamePolygon(polygon.id, e.target.value)
                            }
                            variant="unstyled"
                            size="xs"
                            fw={500}
                            styles={{
                              input: {
                                padding: 0,
                                height: "auto",
                                minHeight: "auto",
                              },
                            }}
                            onClick={(e) => e.stopPropagation()}
                          />
                        </Group>
                        <ActionIcon
                          variant="subtle"
                          color="red"
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            deletePolygon(polygon.id);
                          }}
                        >
                          <AppIcon iconName="Trash" size={14} />
                        </ActionIcon>
                      </Group>
                      <Text size="xs" c="dimmed">
                        {polygon.points.length} points
                      </Text>
                    </Paper>
                  ))}
                </Stack>
              )}
            </ScrollArea>

            {/* Selected Polygon Details */}
            {selectedPolygon && (
              <Box
                p="sm"
                style={{ borderTop: "1px solid var(--mantine-color-gray-2)" }}
              >
                <Group justify="space-between" mb="xs">
                  <Text size="sm" fw={600}>
                    Points ({selectedPolygon.points.length}
                    {maxPointsPerPolygon !== null
                      ? `/${maxPointsPerPolygon}`
                      : ""}
                    )
                  </Text>
                  <Tooltip
                    label={
                      canAddPointToSelectedPolygon
                        ? "Add point after selected (or at end)"
                        : "Point limit reached"
                    }
                  >
                    <ActionIcon
                      variant="subtle"
                      size="sm"
                      onClick={() => addPointToPolygon(selectedPolygon.id)}
                      disabled={!canAddPointToSelectedPolygon}
                    >
                      <AppIcon iconName="Plus" size={14} />
                    </ActionIcon>
                  </Tooltip>
                </Group>
                <Text size="xs" c="dimmed" mb="xs">
                  Double-click on an edge to insert a point there
                </Text>
                <ScrollArea h={150}>
                  <Stack gap={4}>
                    {selectedPolygon.points.map((point, index) => {
                      const isActive = activePointIndex === index;
                      return (
                        <Group
                          key={index}
                          gap="xs"
                          justify="space-between"
                          onClick={() => setActivePointIndex(index)}
                          style={{
                            cursor: "pointer",
                            padding: "4px 8px",
                            borderRadius: 4,
                            backgroundColor: isActive
                              ? "var(--mantine-color-yellow-1)"
                              : "transparent",
                            border: isActive
                              ? "1px solid var(--mantine-color-yellow-4)"
                              : "1px solid transparent",
                            transition: "all 0.15s ease",
                          }}
                        >
                          <Text
                            size="xs"
                            c={isActive ? "dark" : "dimmed"}
                            fw={isActive ? 600 : 400}
                            style={{ fontFamily: "monospace" }}
                          >
                            {index + 1}. {formatCoordinate(point.lat)},{" "}
                            {formatCoordinate(point.lng)}
                          </Text>
                          {selectedPolygon.points.length > 3 && (
                            <ActionIcon
                              variant="subtle"
                              color="red"
                              size="xs"
                              onClick={(e) => {
                                e.stopPropagation();
                                removePointFromPolygon(
                                  selectedPolygon.id,
                                  index
                                );
                                // Reset active point if we're removing it
                                if (activePointIndex === index) {
                                  setActivePointIndex(null);
                                } else if (
                                  activePointIndex !== null &&
                                  activePointIndex > index
                                ) {
                                  // Adjust active point index if we removed a point before it
                                  setActivePointIndex(activePointIndex - 1);
                                }
                              }}
                            >
                              <AppIcon iconName="X" size={12} />
                            </ActionIcon>
                          )}
                        </Group>
                      );
                    })}
                  </Stack>
                </ScrollArea>
              </Box>
            )}

            {/* Footer Actions */}
            <Box
              p="md"
              style={{ borderTop: "1px solid var(--mantine-color-gray-2)" }}
            >
              <Group justify="flex-end" gap="sm">
                <AppButton variant="secondary" onClick={handleCancel}>
                  Cancel
                </AppButton>
                <AppButton variant="primary" onClick={handleSave}>
                  Save Regions
                </AppButton>
              </Group>
            </Box>
          </Stack>
        </Paper>

        {/* Map Container */}
        <Box flex={1} h="100%" pos="relative">
          {error ? (
            <Stack align="center" justify="center" h="100%">
              <AppIcon iconName="AlertTriangle" size={48} color="red" />
              <Text c="red">{error}</Text>
            </Stack>
          ) : (
            <div
              ref={mapContainerRef}
              style={{ width: "100%", height: "100%" }}
            />
          )}

          {/* Drawing Instructions Overlay */}
          {isDrawing && mapLoaded && (
            <Paper
              pos="absolute"
              top={16}
              left="50%"
              style={{ transform: "translateX(-50%)" }}
              p="sm"
              shadow="md"
            >
              <Text size="sm">
                {drawingPoints.length === 0
                  ? "Click on the map to start drawing"
                  : drawingPoints.length < 3
                  ? `Add ${
                      3 - drawingPoints.length
                    } more point(s) to create a polygon`
                  : "Click the first point to close, or continue adding points"}
              </Text>
            </Paper>
          )}
        </Box>
      </Group>
    </Modal>
  );
};
